import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { GulaClient } from "../gulaTypes";
import type {
  EmailBlock,
  EmailBlockType,
  EmailContent,
  EmailStyle,
} from "../../../../../supabase/functions/_shared/gulaEmailRender.ts";

export {
  emailProblems,
  renderEmail,
  youtubeThumbnail,
} from "../../../../../supabase/functions/_shared/gulaEmailRender.ts";
export type { EmailBlock, EmailBlockType, EmailContent, EmailStyle };

export const EMAIL_PATH = "/correo";

export type Segment = "clients" | "leads";
export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "cancelled";

export interface EmailTemplate extends EmailContent {
  id: number;
  name: string;
  audience: Segment | "all";
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface EmailCampaign extends EmailContent {
  id: number;
  name: string;
  segment: Segment;
  status: CampaignStatus;
  template_id: number | null;
  created_by: number | null;
  sent_by: number | null;
  scheduled_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  paused_until: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  // gula_email_campaigns_summary
  total?: number;
  pending?: number;
  sent?: number;
  failed?: number;
  skipped?: number;
  opened?: number;
  clicked?: number;
  unsubscribed?: number;
}

export interface EmailSend {
  id: number;
  campaign_id: number;
  client_id: number | null;
  contact_id: number | null;
  email: string;
  first_name: string | null;
  status: "queued" | "sending" | "sent" | "failed" | "cancelled" | "skipped";
  sent_at: string | null;
  error: string | null;
  opened_at: string | null;
  open_count: number;
  clicked_at: string | null;
  click_count: number;
  unsubscribed_at: string | null;
}

export const SEGMENTS: {
  value: Segment;
  label: string;
  hint: string;
}[] = [
  {
    value: "leads",
    label: "Por convertir",
    hint: "Leads que aún no han comprado: ofertas, catálogo, vídeos, ganchos de compra.",
  },
  {
    value: "clients",
    label: "Clientes",
    hint: "Ya compraron: marca, consejos, noticias del agua, mantenimiento, recomendaciones.",
  },
];

export const STYLES: { value: EmailStyle; label: string; hint: string }[] = [
  {
    value: "brand",
    label: "Diseño Gula",
    hint: "Logo, tarjeta blanca y pie con WhatsApp. Para ofertas y novedades.",
  },
  {
    value: "plain",
    label: "Correo sencillo",
    hint: "Parece un correo escrito a mano. Para textos cortos y cercanos.",
  },
  {
    value: "html",
    label: "Mi diseño (HTML / imágenes)",
    hint: "Solo lo que pongas (HTML de Claude, imágenes de Canva) y el pie legal.",
  },
];

export const STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Borrador",
  scheduled: "Programada",
  sending: "Enviando",
  sent: "Enviada",
  cancelled: "Cancelada",
};

export const STATUS_CLASS: Record<CampaignStatus, string> = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-sky-100 text-sky-800 dark:bg-sky-400/15 dark:text-sky-200",
  sending:
    "bg-amber-100 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200",
  sent: "bg-lime-200 text-neutral-900 dark:bg-lime-400/20 dark:text-lime-200",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-400/15 dark:text-red-200",
};

export const BLOCK_TYPES: {
  type: EmailBlockType;
  label: string;
  hint: string;
}[] = [
  { type: "heading", label: "Título", hint: "Frase grande" },
  { type: "text", label: "Texto", hint: "Párrafos, negrita y enlaces" },
  { type: "image", label: "Imagen", hint: "Sube un PNG/JPG (Canva)" },
  { type: "button", label: "Botón", hint: "Llamada a la acción" },
  { type: "video", label: "Vídeo", hint: "YouTube, Instagram, TikTok…" },
  { type: "file", label: "Archivo", hint: "Catálogo PDF para descargar" },
  { type: "html", label: "HTML", hint: "Pega un diseño hecho con Claude" },
  { type: "divider", label: "Separador", hint: "Línea fina" },
];

const blockId = () => Math.random().toString(36).slice(2, 10);

export const newBlock = (type: EmailBlockType): EmailBlock => {
  const id = blockId();
  switch (type) {
    case "heading":
      return { id, type, text: "" };
    case "text":
      return { id, type, text: "" };
    case "image":
      return { id, type, url: "", alt: "", link: "" };
    case "button":
      return { id, type, label: "", url: "", variant: "dark" };
    case "video":
      return { id, type, url: "", thumbnail: "", label: "Ver el vídeo" };
    case "file":
      return { id, type, url: "", name: "", label: "" };
    case "html":
      return { id, type, html: "" };
    case "divider":
      return { id, type };
  }
};

/** Gives every block a stable id for the editor. */
export const withIds = (blocks: EmailBlock[] = []): EmailBlock[] =>
  blocks.map((block) => (block.id ? block : { ...block, id: blockId() }));

/** A fresh email so nobody starts from a blank page. */
export const starterBlocks = (segment: Segment): EmailBlock[] =>
  withIds(
    segment === "clients"
      ? [
          { type: "heading", text: "Hola {nombre}, ¿qué tal tu agua?" },
          {
            type: "text",
            text: "Escribe aquí la novedad, el consejo o la noticia que quieres contar.",
          },
          {
            type: "button",
            label: "Escríbenos por WhatsApp",
            url: "https://wa.me/34614404301",
          },
        ]
      : [
          { type: "heading", text: "{nombre}, agua pura en casa por 499 €" },
          {
            type: "text",
            text: "Escribe aquí el gancho: la oferta, para quién es y por qué ahora.",
          },
          {
            type: "button",
            label: "Quiero mi equipo",
            url: "https://guladeagua.es",
          },
        ],
  );

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const hasValidEmail = (client: Pick<GulaClient, "email">) =>
  !!client.email && EMAIL_RE.test(client.email.trim());

/** Customers are the ones who bought; everybody else is still to convert. */
export const segmentOf = (client: Pick<GulaClient, "stage">): Segment =>
  client.stage === "purchased" ? "clients" : "leads";

// ---------------------------------------------------------------------------
// Calls to the email_campaign function and storage
// ---------------------------------------------------------------------------

const invoke = async <T>(body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await getSupabaseClient().functions.invoke<T>(
    "email_campaign",
    { method: "POST", body },
  );
  if (error) {
    let message = "No se ha podido";
    try {
      const response = await (error as { context?: Response }).context?.json();
      if (response?.message) message = response.message;
    } catch {
      // keep the generic message
    }
    throw new Error(message);
  }
  return data as T;
};

export const sendTestEmail = (content: EmailContent) =>
  invoke<{ ok: boolean; to: string }>({ action: "test", content });

export const sendCampaign = (
  campaignId: number,
  clientIds: (string | number)[],
  scheduledAt?: string | null,
) =>
  invoke<{
    ok: boolean;
    queued: number;
    invalid: number;
    unsubscribed: number;
    repeated: number;
    scheduled_at: string | null;
  }>({
    action: "send",
    campaign_id: campaignId,
    client_ids: clientIds.map(Number),
    scheduled_at: scheduledAt ?? null,
  });

export const cancelCampaign = (campaignId: number) =>
  invoke<{ ok: boolean }>({ action: "cancel", campaign_id: campaignId });

/** Uploads an image or PDF for an email; returns its public URL. */
export const uploadEmailAsset = async (file: File): Promise<string> => {
  const safeName = file.name.replace(/[^\w.-]+/g, "_");
  const path = `gula/email/${crypto.randomUUID()}-${safeName}`;
  const storage = getSupabaseClient().storage.from("attachments");
  const { error } = await storage.upload(path, file, {
    contentType: file.type || undefined,
    cacheControl: "31536000",
  });
  if (error) throw new Error(`No se ha podido subir ${file.name}`);
  return storage.getPublicUrl(path).data.publicUrl;
};

export const percent = (part = 0, total = 0) =>
  total ? `${Math.round((part / total) * 100)} %` : "–";

export const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
